"use client";

import {useCallback, useEffect, useMemo, useState} from "react";
import {usePathname} from "next/navigation";
import {useTranslation} from "react-i18next";
import {useRouter} from "@bprogress/next";
import {useArtifactPanel} from "@/contexts/chat/ArtifactPanelContext";
import {useGailDock} from "@/contexts/chat/GailDockContext";
import {useScriptVersioningEnabled} from "@/hooks/useScriptVersioningEnabled";
import {apiFetch, apiFetchWithStatus} from "@/lib/api";
import type {ScriptDraft} from "@/services/wingman";
import type {VariablesResponse} from "@/types/agencies";
import type {GailError} from "@/types/api";
import {GAIL_API} from "@/utils/common/constants/routes";
import {type ApiFunction, saveNewScript} from "@/utils/inbound/scriptSave";
import {activeChatConversationId} from "@/utils/chat/scriptNavigation";
import {notifyScriptListRefresh} from "@/utils/scripts/scriptListRefresh";
import {
   analyzeDraftVariables,
   buildScriptSavePayload,
   getScriptDetailHref,
   type ScriptDraftDirection,
   type ScriptDraftVoice,
} from "./scriptDraftSave";
import {SCRIPT_API_VERSION} from "@/enums/profile";

export type ScriptDraftSaveState =
   | {status: "idle"}
   | {status: "saving"}
   | {status: "saved"; scriptId: string; detailHref: string}
   | {status: "error"; details: string[]};

/**
 * `ApiFunction` wrapper that collects backend validation messages from
 * failed responses into `collected`. `saveNewScript`'s contract only
 * reports which step failed, so the actual Galactus error details have to
 * be captured at the request boundary to be shown in the panel.
 */
const collectingApiCall =
   (collected: string[]): ApiFunction =>
   async <TOutput,>(
      url: string,
      method: "GET" | "PUT" | "POST" | "DELETE" | "PATCH" = "GET",
      options?: {body?: unknown}
   ): Promise<TOutput | null> => {
      const response = await apiFetchWithStatus<TOutput>(url, method, {body: options?.body});
      if (response.success) return response.data;

      const errors = (response.data as Partial<GailError> | null)?.errors;
      errors?.forEach((error) => {
         const text = error.detail || error.title;
         if (text) collected.push(text);
      });
      return null;
   };

/**
 * Save flow for a script draft shown in the workspace: normalizes the draft's
 * variables against the org's contact fields, creates the use cases and the
 * phone script through the user's session, and reports the outcome back into
 * the conversation so the assistant has it in context.
 *
 * Save outcomes are recorded on the panel provider keyed by `artifactId` (the
 * draft's callId), so reopening an already-saved draft shows its saved state
 * instead of offering a second save that would duplicate the script.
 *
 * `enabled` skips the variable-fields fetch when the save action isn't
 * available on this surface (no permission / gpt-only org).
 */
export const useScriptDraftSave = (draft: ScriptDraft, artifactId: string, enabled: boolean) => {
   const {t} = useTranslation("ask");
   const pathname = usePathname();
   const router = useRouter();
   const {isGailGptDockEnabled, conversationId: dockConversationId, showConversationOnRight} =
      useGailDock();
   const {sendChatMessage, savedDrafts} = useArtifactPanel();
   const scriptVersioningEnabled = useScriptVersioningEnabled();
   const [state, setState] = useState<ScriptDraftSaveState>(() => {
      const saved = savedDrafts.get(artifactId);
      return saved ? {status: "saved", ...saved} : {status: "idle"};
   });
   const [fixRequested, setFixRequested] = useState(false);
   // null = fields unavailable (still loading or fetch failed): save proceeds
   // without normalization and without a misleading "all unknown" warning.
   const [orgFields, setOrgFields] = useState<string[] | null>(null);

   useEffect(() => {
      if (!enabled) return undefined;
      let didCancel = false;

      const loadFields = async () => {
         const response = await apiFetch<VariablesResponse>(
            `${GAIL_API}/v1/contacts/variable_fields`,
            "GET",
            {silent: true}
         );
         if (!didCancel && response) setOrgFields(response);
      };

      void loadFields();
      return () => {
         didCancel = true;
      };
   }, [enabled]);

   const analysis = useMemo(
      () => (orgFields ? analyzeDraftVariables(draft, orgFields) : null),
      [draft, orgFields]
   );

   const save = useCallback(
      async (agentName: string, direction: ScriptDraftDirection, voice: ScriptDraftVoice) => {
         setState({status: "saving"});

         const {script, useCases} = buildScriptSavePayload(
            draft,
            analysis?.known ?? new Map(),
            agentName,
            direction,
            voice,
            scriptVersioningEnabled
         );
         const errorDetails: string[] = [];
         const result = await saveNewScript(
            script,
            undefined,
            useCases,
            collectingApiCall(errorDetails),
            undefined,
            scriptVersioningEnabled ? SCRIPT_API_VERSION.V2 : SCRIPT_API_VERSION.V1
         );

         if (!result.success) {
            setState({status: "error", details: errorDetails});
            return;
         }

         const saved = {
            scriptId: result.scriptId,
            detailHref: getScriptDetailHref(direction, result.scriptId, scriptVersioningEnabled),
         };
         savedDrafts.set(artifactId, saved);
         setState({status: "saved", ...saved});
         notifyScriptListRefresh({scriptId: result.scriptId, direction});
         const savedMessage = t("workspace.scriptDraft.savedMessage", {name: draft.scriptName});
         const conversationId = activeChatConversationId(pathname, dockConversationId);
         if (isGailGptDockEnabled && conversationId) {
            showConversationOnRight(conversationId, savedMessage);
            router.push(saved.detailHref);
         } else {
            sendChatMessage(savedMessage);
         }
      },
      [
         draft,
         artifactId,
         analysis,
         savedDrafts,
         sendChatMessage,
         t,
         scriptVersioningEnabled,
         pathname,
         dockConversationId,
         isGailGptDockEnabled,
         showConversationOnRight,
         router,
      ]
   );

   /**
    * Asks the assistant (in the user's language) to revise the draft so it
    * only uses the org's real contact fields. The field list is included in
    * the message because the model has no tool to look it up. The revision
    * arrives as a new draft, which replaces this one in the workspace.
    */
   const requestVariableFix = useCallback(() => {
      const unknown = analysis?.unknown ?? [];
      if (unknown.length === 0) return;

      const variables = unknown.map((name) => `{${name}}`).join(", ");
      const fields = (orgFields ?? []).join(", ");
      sendChatMessage(
         fields
            ? t("workspace.scriptDraft.fixVariablesMessage", {variables, fields})
            : t("workspace.scriptDraft.fixVariablesMessageNoFields", {variables})
      );
      setFixRequested(true);
   }, [analysis, orgFields, sendChatMessage, t]);

   return {
      state,
      save,
      unknownVariables: analysis?.unknown ?? [],
      requestVariableFix,
      fixRequested,
   };
};
